/**
 * DocumentScanner — Otsu luminance segmentation + projection histograms.
 * Detects the document as the dominant bright region against a dark background.
 * Live camera via getUserMedia. Falls back to gallery if camera is unavailable.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import {
  Camera,
  Check,
  RotateCcw,
  X,
  Loader2,
  AlertCircle,
  Crop,
} from "lucide-react";
import { Button } from "@/components/ui";

// ─────────────────────────────────────────────
// CV helpers
// ─────────────────────────────────────────────

function toGray(data: Uint8ClampedArray, n: number): Uint8Array {
  const g = new Uint8Array(n);
  for (let i = 0; i < n; i++)
    g[i] =
      (0.299 * data[i * 4] +
        0.587 * data[i * 4 + 1] +
        0.114 * data[i * 4 + 2]) |
      0;
  return g;
}

/** Otsu's method — finds the threshold that best separates dark background
 *  from the bright document. Returns a value in [0, 255]. */
function otsuThreshold(gray: Uint8Array, n: number): number {
  const hist = new Int32Array(256);
  for (let i = 0; i < n; i++) hist[gray[i]]++;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0,
    wB = 0,
    maxVar = 0,
    thresh = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (!wB) continue;
    const wF = n - wB;
    if (!wF) break;
    sumB += t * hist[t];
    const mB = sumB / wB,
      mF = (sum - sumB) / wF;
    const v = wB * wF * (mB - mF) ** 2;
    if (v > maxVar) {
      maxVar = v;
      thresh = t;
    }
  }
  return thresh;
}

function smooth1D(arr: Float32Array, r: number): Float32Array {
  const o = new Float32Array(arr.length);
  for (let i = 0; i < arr.length; i++) {
    let s = 0,
      n = 0;
    for (let k = -r; k <= r; k++) {
      const j = i + k;
      if (j >= 0 && j < arr.length) {
        s += arr[j];
        n++;
      }
    }
    o[i] = s / n;
  }
  return o;
}

interface DocBounds {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Detects document bounding box using Otsu + projection histograms.
 *
 * For each column x: brightFrac[x] = (bright pixels in column) / column_height
 * The document creates a "plateau" in this profile. We find where the plateau
 * starts and ends using FWHM (half-maximum) style threshold.
 *
 * Key difference from Canny+projection: we're looking at luminance mass
 * (how much of the column is bright), not edges. This is unaffected by
 * video frame border artifacts.
 */
function detectDocBounds(
  gray: Uint8Array,
  w: number,
  h: number,
): DocBounds | null {
  const n = w * h;
  const t = otsuThreshold(gray, n);

  // If the image is overwhelmingly dark or bright, there's no clear document
  if (t < 18 || t > 240) return null;

  // Guard zone: exclude outer 7% of frame on each side to avoid video-border artifacts
  const gx = Math.max(4, (w * 0.07) | 0);
  const gy = Math.max(4, (h * 0.07) | 0);
  const validH = h - 2 * gy;
  const validW = w - 2 * gx;

  // Build column/row bright-pixel fraction profiles (inside guard zone)
  const fX = new Float32Array(w),
    fY = new Float32Array(h);
  for (let y = gy; y < h - gy; y++)
    for (let x = gx; x < w - gx; x++)
      if (gray[y * w + x] >= t) {
        fX[x]++;
        fY[y]++;
      }

  for (let x = 0; x < w; x++) fX[x] /= validH;
  for (let y = 0; y < h; y++) fY[y] /= validW;

  // Smooth to reduce noise
  const sfX = smooth1D(fX, 6),
    sfY = smooth1D(fY, 6);

  // Find peak bright-fraction (= fraction of column height that is bright at the document center)
  let peakX = 0,
    peakY = 0;
  for (let x = gx; x < w - gx; x++) if (sfX[x] > peakX) peakX = sfX[x];
  for (let y = gy; y < h - gy; y++) if (sfY[y] > peakY) peakY = sfY[y];

  // The document must create a meaningful bright region (>20% of column/row)
  if (peakX < 0.2 || peakY < 0.15) return null;

  // FWHM-style: find bounds at 40% of peak (adapted to document fill)
  const thX = peakX * 0.4;
  const thY = peakY * 0.4;

  let x1 = -1,
    x2 = -1,
    y1 = -1,
    y2 = -1;
  for (let x = gx; x < w - gx; x++)
    if (sfX[x] >= thX) {
      if (x1 < 0) x1 = x;
      x2 = x;
    }
  for (let y = gy; y < h - gy; y++)
    if (sfY[y] >= thY) {
      if (y1 < 0) y1 = y;
      y2 = y;
    }

  if (x1 < 0 || y1 < 0) return null;

  const bw = x2 - x1,
    bh = y2 - y1;
  // Reject detections that are too small, too large (= frame itself), or wrong aspect ratio
  if (bw < w * 0.15 || bh < h * 0.1) return null;
  if (bw > w * 0.92 && bh > h * 0.92) return null; // full frame = false detection
  const ar = bw / bh;
  if (ar < 0.7 || ar > 4.0) return null;

  return { x1, y1, x2, y2 };
}

/** Run detection pipeline on a live video frame */
function analyzeFrame(video: HTMLVideoElement): DocBounds | null {
  const vw = video.videoWidth,
    vh = video.videoHeight;
  if (!vw || !vh || video.readyState < 2) return null;

  const scale = Math.min(1, 360 / Math.max(vw, vh));
  const aw = (vw * scale) | 0,
    ah = (vh * scale) | 0;

  const ac = document.createElement("canvas");
  ac.width = aw;
  ac.height = ah;
  ac.getContext("2d")!.drawImage(video, 0, 0, vw, vh, 0, 0, aw, ah);
  const { data } = ac.getContext("2d")!.getImageData(0, 0, aw, ah);

  const b = detectDocBounds(toGray(data, aw * ah), aw, ah);
  if (!b) return null;

  return {
    x1: (b.x1 / scale) | 0,
    y1: (b.y1 / scale) | 0,
    x2: (b.x2 / scale) | 0,
    y2: (b.y2 / scale) | 0,
  };
}

/** Captura el frame completo del video (sin recortar). */
async function captureFullFrame(
  video: HTMLVideoElement,
): Promise<{ url: string; w: number; h: number }> {
  const vw = video.videoWidth,
    vh = video.videoHeight;
  const c = document.createElement("canvas");
  c.width = vw;
  c.height = vh;
  c.getContext("2d")!.drawImage(video, 0, 0, vw, vh);
  const url = c.toDataURL("image/jpeg", 0.92);
  return { url, w: vw, h: vh };
}

/** Recorta una imagen (dataURL) a las bounds especificadas y devuelve File + dataURL. */
async function cropImageToFile(
  imageUrl: string,
  bounds: DocBounds,
): Promise<{ url: string; file: File }> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("img load"));
    img.src = imageUrl;
  });
  const cw = Math.max(1, bounds.x2 - bounds.x1);
  const ch = Math.max(1, bounds.y2 - bounds.y1);
  const c = document.createElement("canvas");
  c.width = cw;
  c.height = ch;
  c.getContext("2d")!.drawImage(
    img,
    bounds.x1,
    bounds.y1,
    cw,
    ch,
    0,
    0,
    cw,
    ch,
  );
  const url = c.toDataURL("image/jpeg", 0.92);
  const blob = await new Promise<Blob>((res, rej) =>
    c.toBlob(
      (b) => (b ? res(b) : rej(new Error("toBlob"))),
      "image/jpeg",
      0.92,
    ),
  );
  return {
    url,
    file: new File([blob], `doc-${Date.now()}.jpg`, { type: "image/jpeg" }),
  };
}

// ─────────────────────────────────────────────
// Crop overlay (manual adjust)
// ─────────────────────────────────────────────

type Handle = "move" | "tl" | "tr" | "bl" | "br";

interface CropOverlayProps {
  imageUrl: string;
  imageW: number;
  imageH: number;
  bounds: DocBounds;
  onChange: (b: DocBounds) => void;
}

/** Overlay con rectángulo arrastrable (4 esquinas + cuerpo) sobre una imagen.
 *  Las coordenadas trabajan en el espacio nativo de la imagen. */
function CropOverlay({
  imageUrl,
  imageW,
  imageH,
  bounds,
  onChange,
}: CropOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    handle: Handle;
    startX: number;
    startY: number;
    initial: DocBounds;
    scale: number;
  } | null>(null);

  const clamp = (b: DocBounds): DocBounds => {
    const minSize = 30;
    let { x1, y1, x2, y2 } = b;
    x1 = Math.max(0, Math.min(x1, imageW - minSize));
    y1 = Math.max(0, Math.min(y1, imageH - minSize));
    x2 = Math.max(x1 + minSize, Math.min(x2, imageW));
    y2 = Math.max(y1 + minSize, Math.min(y2, imageH));
    return { x1, y1, x2, y2 };
  };

  const onPointerDown = (handle: Handle) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const cont = containerRef.current;
    if (!cont) return;
    const rect = cont.getBoundingClientRect();
    const scale = rect.width / imageW;
    dragRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      initial: { ...bounds },
      scale,
    };
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.scale;
    const dy = (e.clientY - d.startY) / d.scale;
    const i = d.initial;
    let next: DocBounds = { ...i };
    switch (d.handle) {
      case "move": {
        const w = i.x2 - i.x1;
        const h = i.y2 - i.y1;
        next.x1 = i.x1 + dx;
        next.y1 = i.y1 + dy;
        next.x2 = next.x1 + w;
        next.y2 = next.y1 + h;
        // mantener dentro de la imagen sin cambiar tamaño
        if (next.x1 < 0) {
          next.x1 = 0;
          next.x2 = w;
        }
        if (next.y1 < 0) {
          next.y1 = 0;
          next.y2 = h;
        }
        if (next.x2 > imageW) {
          next.x2 = imageW;
          next.x1 = imageW - w;
        }
        if (next.y2 > imageH) {
          next.y2 = imageH;
          next.y1 = imageH - h;
        }
        break;
      }
      case "tl":
        next.x1 = i.x1 + dx;
        next.y1 = i.y1 + dy;
        break;
      case "tr":
        next.x2 = i.x2 + dx;
        next.y1 = i.y1 + dy;
        break;
      case "bl":
        next.x1 = i.x1 + dx;
        next.y2 = i.y2 + dy;
        break;
      case "br":
        next.x2 = i.x2 + dx;
        next.y2 = i.y2 + dy;
        break;
    }
    onChange(clamp(next));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    dragRef.current = null;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };

  // Coordenadas porcentuales para el overlay
  const left = (bounds.x1 / imageW) * 100;
  const top = (bounds.y1 / imageH) * 100;
  const width = ((bounds.x2 - bounds.x1) / imageW) * 100;
  const height = ((bounds.y2 - bounds.y1) / imageH) * 100;

  return (
    <div
      ref={containerRef}
      className="relative w-full select-none touch-none"
      style={{ aspectRatio: `${imageW} / ${imageH}` }}
    >
      <img
        src={imageUrl}
        alt="frame"
        className="absolute inset-0 w-full h-full object-contain"
        draggable={false}
      />
      {/* Sombras (4 rectángulos alrededor del crop) */}
      <div
        className="absolute bg-black/55 pointer-events-none"
        style={{ left: 0, top: 0, right: 0, height: `${top}%` }}
      />
      <div
        className="absolute bg-black/55 pointer-events-none"
        style={{
          left: 0,
          top: `${top}%`,
          width: `${left}%`,
          height: `${height}%`,
        }}
      />
      <div
        className="absolute bg-black/55 pointer-events-none"
        style={{
          left: `${left + width}%`,
          top: `${top}%`,
          right: 0,
          height: `${height}%`,
        }}
      />
      <div
        className="absolute bg-black/55 pointer-events-none"
        style={{
          left: 0,
          top: `${top + height}%`,
          right: 0,
          bottom: 0,
        }}
      />
      {/* Rectángulo */}
      <div
        className="absolute border-2 border-green-400 cursor-move"
        style={{
          left: `${left}%`,
          top: `${top}%`,
          width: `${width}%`,
          height: `${height}%`,
        }}
        onPointerDown={onPointerDown("move")}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* Handles */}
        {(["tl", "tr", "bl", "br"] as const).map((h) => (
          <div
            key={h}
            onPointerDown={onPointerDown(h)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className={`absolute w-7 h-7 rounded-full bg-green-400 border-2 border-white shadow-lg ${
              h === "tl"
                ? "-top-3.5 -left-3.5 cursor-nwse-resize"
                : h === "tr"
                  ? "-top-3.5 -right-3.5 cursor-nesw-resize"
                  : h === "bl"
                    ? "-bottom-3.5 -left-3.5 cursor-nesw-resize"
                    : "-bottom-3.5 -right-3.5 cursor-nwse-resize"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

interface DocumentScannerProps {
  label: string;
  onCapture: (file: File) => void;
  onClose: () => void;
}

type Phase = "loading" | "camera" | "adjust" | "preview" | "error";

export default function DocumentScanner({
  label,
  onCapture,
  onClose,
}: DocumentScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const boundsRef = useRef<DocBounds | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [detected, setDetected] = useState(false);
  const [dispBounds, setDispBounds] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [capturedFile, setCapturedFile] = useState<File | null>(null);

  // Estado del crop manual (fase "adjust")
  const [fullFrameUrl, setFullFrameUrl] = useState("");
  const [fullFrameSize, setFullFrameSize] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const [adjustBounds, setAdjustBounds] = useState<DocBounds | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setPhase("loading");
    setDetected(false);
    setDispBounds(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setPhase("camera");
    } catch {
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopStream();
  }, [startCamera, stopStream]);

  // Detection loop — every 350ms
  useEffect(() => {
    if (phase !== "camera") return;
    const id = setInterval(() => {
      const v = videoRef.current;
      if (!v || v.readyState < 2) return;
      const b = analyzeFrame(v);
      boundsRef.current = b;
      setDetected(!!b);
      if (b) {
        const vw = v.videoWidth,
          vh = v.videoHeight;
        setDispBounds({
          x: (b.x1 / vw) * 100,
          y: (b.y1 / vh) * 100,
          w: ((b.x2 - b.x1) / vw) * 100,
          h: ((b.y2 - b.y1) / vh) * 100,
        });
      } else {
        setDispBounds(null);
      }
    }, 350);
    return () => clearInterval(id);
  }, [phase]);

  const handleCapture = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return;
    const frame = await captureFullFrame(v);
    setFullFrameUrl(frame.url);
    setFullFrameSize({ w: frame.w, h: frame.h });
    // Crop inicial: bounds detectados o un rectángulo central por defecto
    const initial: DocBounds = boundsRef.current ?? {
      x1: (frame.w * 0.06) | 0,
      y1: (frame.h * 0.2) | 0,
      x2: (frame.w * 0.94) | 0,
      y2: (frame.h * 0.8) | 0,
    };
    setAdjustBounds(initial);
    stopStream();
    setPhase("adjust");
  }, [stopStream]);

  const handleApplyCrop = useCallback(async () => {
    if (!adjustBounds || !fullFrameUrl) return;
    const { url, file } = await cropImageToFile(fullFrameUrl, adjustBounds);
    setPreviewUrl(url);
    setCapturedFile(file);
    setPhase("preview");
  }, [adjustBounds, fullFrameUrl]);

  const handleRetake = useCallback(() => {
    if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setCapturedFile(null);
    setFullFrameUrl("");
    setFullFrameSize(null);
    setAdjustBounds(null);
    startCamera();
  }, [previewUrl, startCamera]);

  const handleAdjustAgain = useCallback(() => {
    if (fullFrameUrl) {
      setPhase("adjust");
    } else {
      handleRetake();
    }
  }, [fullFrameUrl, handleRetake]);

  const handleUse = useCallback(() => {
    if (!capturedFile) return;
    onCapture(capturedFile);
    onClose();
  }, [capturedFile, onCapture, onClose]);

  const handleFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    // Cargar el archivo como dataURL completo y pasar a fase "adjust" para
    // permitir recorte manual antes de confirmar.
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      const img = new Image();
      img.onload = () => {
        setFullFrameUrl(url);
        setFullFrameSize({ w: img.naturalWidth, h: img.naturalHeight });
        setAdjustBounds({
          x1: (img.naturalWidth * 0.04) | 0,
          y1: (img.naturalHeight * 0.1) | 0,
          x2: (img.naturalWidth * 0.96) | 0,
          y2: (img.naturalHeight * 0.9) | 0,
        });
        setPhase("adjust");
      };
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      style={{ height: "100dvh" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-6 pb-3 flex-shrink-0">
        <p className="text-white font-semibold text-sm">{label}</p>
        <button
          onClick={() => {
            stopStream();
            onClose();
          }}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Viewport */}
      <div className="flex-1 relative overflow-hidden">
        {/* Video always in DOM to keep the ref */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`absolute inset-0 w-full h-full object-cover${phase === "camera" ? "" : " hidden"}`}
        />

        {phase === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-10 h-10 text-white/50 animate-spin" />
            <p className="text-white/40 text-sm">Iniciando cámara…</p>
          </div>
        )}

        {phase === "camera" && (
          <>
            {/* Detected document box (green) or static guide frame (white/dim) */}
            {dispBounds ? (
              <div
                className="absolute pointer-events-none border-2 border-green-400 rounded-sm transition-all duration-150"
                style={{
                  left: `${dispBounds.x}%`,
                  top: `${dispBounds.y}%`,
                  width: `${dispBounds.w}%`,
                  height: `${dispBounds.h}%`,
                }}
              >
                <span className="absolute -top-px -left-px w-5 h-5 border-t-[3px] border-l-[3px] border-green-400" />
                <span className="absolute -top-px -right-px w-5 h-5 border-t-[3px] border-r-[3px] border-green-400" />
                <span className="absolute -bottom-px -left-px w-5 h-5 border-b-[3px] border-l-[3px] border-green-400" />
                <span className="absolute -bottom-px -right-px w-5 h-5 border-b-[3px] border-r-[3px] border-green-400" />
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="relative border border-white/30 rounded-lg"
                  style={{
                    width: "88%",
                    aspectRatio: "1.6",
                    boxShadow: "0 0 0 100vmax rgba(0,0,0,0.42)",
                  }}
                >
                  <span className="absolute -top-0.5 -left-0.5 w-5 h-5 border-t-2 border-l-2 border-white/60" />
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 border-t-2 border-r-2 border-white/60" />
                  <span className="absolute -bottom-0.5 -left-0.5 w-5 h-5 border-b-2 border-l-2 border-white/60" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 border-b-2 border-r-2 border-white/60" />
                </div>
              </div>
            )}

            {/* Status badge */}
            <div className="absolute top-4 inset-x-0 flex justify-center">
              <div
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  detected
                    ? "bg-green-500/85 text-white"
                    : "bg-black/50 text-white/50"
                }`}
              >
                {detected
                  ? "Documento detectado"
                  : "Apunta la cámara al documento"}
              </div>
            </div>

            <p className="absolute bottom-28 inset-x-0 text-center text-white/40 text-xs pointer-events-none">
              {detected
                ? "Se recortará automáticamente al capturar"
                : "Usa fondo oscuro y buena iluminación"}
            </p>

            {/* Shutter button */}
            <div className="absolute bottom-8 inset-x-0 flex justify-center">
              <button
                type="button"
                onClick={handleCapture}
                className={`w-16 h-16 rounded-full border-[3px] flex items-center justify-center shadow-lg active:scale-95 transition-all ${
                  detected
                    ? "bg-green-400 border-green-200 scale-105"
                    : "bg-white border-white/30"
                }`}
              >
                <Camera
                  className={`w-7 h-7 ${detected ? "text-white" : "text-black"}`}
                />
              </button>
            </div>
          </>
        )}

        {phase === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-8 text-center">
            <AlertCircle className="w-12 h-12 text-white/30" />
            <p className="text-white/60 text-sm">
              Sin acceso a la cámara. Puedes seleccionar la imagen desde tu
              galería.
            </p>
            <Button
              type="button"
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="w-4 h-4 mr-2" />
              Seleccionar imagen
            </Button>
          </div>
        )}

        {phase === "adjust" &&
          fullFrameUrl &&
          fullFrameSize &&
          adjustBounds && (
            <div className="absolute inset-0 flex flex-col">
              {/* Instrucciones */}
              <div className="flex-shrink-0 px-4 py-3 text-center">
                <p className="text-white/80 text-sm font-medium">
                  Ajusta el recorte arrastrando las esquinas
                </p>
                <p className="text-white/50 text-xs mt-1">
                  Asegúrate de que el documento esté completamente dentro del
                  área
                </p>
              </div>

              {/* Área de crop */}
              <div className="flex-1 flex items-center justify-center px-4 overflow-hidden">
                <div className="w-full max-w-lg">
                  <CropOverlay
                    imageUrl={fullFrameUrl}
                    imageW={fullFrameSize.w}
                    imageH={fullFrameSize.h}
                    bounds={adjustBounds}
                    onChange={setAdjustBounds}
                  />
                </div>
              </div>

              {/* Botones */}
              <div className="flex-shrink-0 flex gap-3 px-6 pb-8 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
                  onClick={handleRetake}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Retomar
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  onClick={handleApplyCrop}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Confirmar
                </Button>
              </div>
            </div>
          )}

        {phase === "preview" && previewUrl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-6">
            <div className="w-full max-w-sm rounded-xl overflow-hidden border border-white/20 shadow-xl">
              <img
                src={previewUrl}
                alt={label}
                className="w-full object-contain"
              />
            </div>
            <div className="flex flex-col gap-3 w-full max-w-sm">
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
                  onClick={handleRetake}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Retomar
                </Button>
                <Button type="button" className="flex-1" onClick={handleUse}>
                  <Check className="w-4 h-4 mr-2" />
                  Usar
                </Button>
              </div>
              {fullFrameUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-white/70 hover:text-white hover:bg-white/10"
                  onClick={handleAdjustAgain}
                >
                  <Crop className="w-4 h-4 mr-2" />
                  Ajustar recorte
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input for gallery fallback */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}
